/**
 * Draws the page. Every function here returns a plain `HTMLElement` built
 * with `document.createElement` and `textContent` — never `innerHTML` — so a
 * value out of the signed manifest can never be read as markup, even though
 * every field is already shape-checked by `versionManifestSchema` before it
 * reaches here.
 */
import {
  buildOpenAppUrl,
  channelLabel,
  findChannelRelease,
  findOtherReleases,
  listChannels,
} from './channels'
import {
  APP_ENTRY_ROUTE,
  buildLocalCopyDownloadUrl,
  type CommunitySite,
  LOCAL_COPY_CHECKSUMS_ASSET,
  LOCAL_COPY_DOWNLOADS,
  LOCAL_COPY_ORIGIN,
  LOCAL_COPY_URL,
  MANIFEST_MIRROR_URL,
  MANIFEST_URL,
  PUBLIC_KEYS_FILE_URL,
  SIGNATURE_MIRROR_URL,
  releasePageUrl,
  RELEASES_REPOSITORY_URL,
  SELF_HOST_DOCS_URL,
} from './config'
import { createAccordion } from './accordion'
import { createElement, createLink, createTextElement } from './dom'
import { buildHomeCards, type HomeCard } from './homeCards'
import { createOpenAppGroup, type OpenAppGroup, type OpenTarget } from './openAppGroup'
import { browserStorage, type LocalCopyStorageSource } from './localCopyMemory'
import { formatPublishedDate, shortCommitFromReleaseId } from './format'
import { buildNeutralReleaseUrl, listNeutralGateways } from './neutralGateway'
import { type VerificationFailureReason, verificationFailureText } from './verify'
import { buildGatewayUrl } from '../../../scripts/ipfs-release/schema'
import type { ReleaseEntry, VersionManifest } from '../../../scripts/ipfs-release/schema'
import snapshotLight from './assets/snapshots/app-light.webp'
import snapshotDark from './assets/snapshots/app-dark.webp'

/**
 * Everything the verified view draws from. `selectedChannel` picks which
 * channel's release the page shows; `onSelectChannel` is how the channel
 * toggle asks for a different one. The page itself holds no state — see
 * `page.ts`, which owns the selection and the local-copy probe.
 */
export interface VerifiedView {
  manifest: VersionManifest
  selectedChannel: string
  localCopy: LocalCopyDisplay
  communitySites: readonly CommunitySite[]
  onSelectChannel: (channel: string) => void
  /** Called when the reader presses "Check for my local copy". */
  onCheckLocalCopy: () => void
  /** Where the reader's default way of opening the app is saved. Defaults to `localStorage`. */
  storage?: LocalCopyStorageSource
}

/**
 * What the hero shows about a local copy:
 * - `idle`: nothing checked yet, or a remembered copy did not answer — just
 *   the quiet "Check for my local copy" control, no error.
 * - `checking`: a probe is running.
 * - `not-found`: the reader pressed the control and nothing answered.
 * - `running`: the "Open your local copy" link.
 */
export type LocalCopyDisplay =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'not-found' }
  | { kind: 'running'; servedReleaseId: string }

/**
 * The four states the page can be in. A discriminated union, so `renderPage`
 * never has to guess which fields are present — `verified` is the only case
 * that carries a manifest, and it is the only case with any link on the page.
 */
export type PageState =
  | { kind: 'loading' }
  | ({ kind: 'verified' } & VerifiedView)
  | { kind: 'verification-failed'; reason: VerificationFailureReason }
  | { kind: 'unreachable' }

/**
 * Toggles a control between "Copy" and "Copied" for 1.5 seconds after a
 * successful clipboard write. Falls back to selecting the text so a reader
 * on a browser without Clipboard API access can still copy it by hand.
 */
const createCopyButton = (valueToCopy: string): HTMLButtonElement => {
  const button = createTextElement('button', 'Copy', 'copy-button')
  button.type = 'button'
  button.addEventListener('click', () => {
    navigator.clipboard
      .writeText(valueToCopy)
      .then(() => {
        button.textContent = 'Copied'
        setTimeout(() => {
          button.textContent = 'Copy'
        }, 1500)
      })
      .catch(() => {
        button.textContent = 'Select the text and copy it'
      })
  })
  return button
}

const createStatusView = (heading: string, body: string, state: string): HTMLElement => {
  const section = createElement('section', 'status-view')
  section.dataset.state = state
  section.append(createTextElement('h1', heading), createTextElement('p', body))
  return section
}

const createLoadingView = (): HTMLElement =>
  createStatusView('Looking for the latest release…', 'This takes a moment.', 'loading')

const createUnreachableView = (): HTMLElement =>
  createStatusView(
    'The release list could not be reached.',
    'The signed release list did not answer. Check your connection, or try again shortly.',
    'unreachable',
  )

const createVerificationFailedView = (reason: VerificationFailureReason): HTMLElement =>
  createStatusView(
    'The release list failed verification.',
    `${verificationFailureText(reason)} No links are shown until the list verifies.`,
    'verification-failed',
  )

const createReleaseSummary = (release: ReleaseEntry, toggle: HTMLElement | null): HTMLElement => {
  const summary = createElement('div', 'release-summary')

  const versionLine = createElement('p', 'release-version')
  versionLine.append(
    createTextElement('span', `v${release.version}`, 'release-version-number'),
    document.createTextNode(' · '),
    createLink(releasePageUrl(release.id), shortCommitFromReleaseId(release.id), 'commit-link'),
    document.createTextNode(' · '),
    createTextElement('time', formatPublishedDate(release.publishedAt)),
  )
  const metaRow = createElement('div', 'release-meta')
  if (toggle !== null) {
    metaRow.append(toggle)
  }
  metaRow.append(versionLine)
  summary.append(metaRow)

  const cidRow = createElement('div', 'cid-row')
  cidRow.append(
    createTextElement('span', 'Content ID', 'cid-label'),
    createTextElement('code', release.cid, 'cid-value'),
    createCopyButton(release.cid),
  )
  summary.append(cidRow)

  return summary
}

/**
 * One link per neutral public gateway for the release shown, in signed-list
 * order. A gateway that Gibs hosts is never listed (see `neutralGateway.ts`),
 * and neither is one that refuses our releases.
 */
const createGatewayList = (manifest: VersionManifest, cid: string): HTMLElement => {
  const list = createElement('ul', 'gateway-list')
  for (const gateway of listNeutralGateways(manifest)) {
    const item = createElement('li')
    item.append(createLink(buildGatewayUrl(gateway, cid), gateway))
    list.append(item)
  }
  return list
}

/** One older release as its own accordion row: its version and date show, its links open. */
const createOlderReleaseRow = (manifest: VersionManifest, release: ReleaseEntry): HTMLElement => {
  const links = createElement('div', 'release-links')
  links.append(
    createLink(releasePageUrl(release.id), 'Release page'),
    // Our node pins every release listed here (findOtherReleases keeps to the
    // pin watcher's own rule). A neutral gateway fetches from that node.
    createLink(buildNeutralReleaseUrl(manifest, release.cid), 'Open'),
  )
  const cidRow = createElement('div', 'cid-row')
  cidRow.append(
    createTextElement('span', 'Content ID', 'cid-label'),
    createTextElement('code', release.cid, 'cid-value'),
    createCopyButton(release.cid),
  )
  const row = createAccordion({
    className: 'release-row',
    title: `v${release.version}`,
    summary: `${shortCommitFromReleaseId(release.id)} · ${formatPublishedDate(release.publishedAt)}`,
    panel: [links, cidRow],
  })
  const item = createElement('li')
  item.append(row)
  return item
}

const createOlderReleasesSection = (
  manifest: VersionManifest,
  shownRelease: ReleaseEntry,
): HTMLElement => {
  const olderReleases = findOtherReleases(manifest, shownRelease)
  const list = createElement('ul', 'release-rows')
  list.append(...olderReleases.map((release) => createOlderReleaseRow(manifest, release)))
  return createAccordion({
    className: 'older-releases',
    title: `Other releases (${olderReleases.length})`,
    summary: 'Older signed releases that our node still pins. Open one for its links.',
    panel: [list],
  })
}

const createVerifyYourselfSection = (cid: string): HTMLElement => {
  const panel: Node[] = []
  panel.push(
    createTextElement(
      'p',
      'This page checked an Ed25519 signature over the release list before showing any link above. Pin the release on your own IPFS node to keep your own copy:',
    ),
  )
  const codeRow = createElement('div', 'code-row')
  codeRow.append(
    createTextElement('code', `ipfs pin add ${cid}`),
    createCopyButton(`ipfs pin add ${cid}`),
  )
  panel.push(codeRow)

  const linksParagraph = createElement('p')
  linksParagraph.append(
    document.createTextNode('The signed list is public: '),
    createLink(MANIFEST_MIRROR_URL, 'version.json'),
    document.createTextNode(', with its signature '),
    createLink(SIGNATURE_MIRROR_URL, 'version.json.sig'),
    document.createTextNode('. Its history is in '),
    createLink(RELEASES_REPOSITORY_URL, 'gibsfinance/interface-releases'),
    document.createTextNode('. The signing key is published there too: '),
    createLink(PUBLIC_KEYS_FILE_URL, 'public-keys.json'),
    document.createTextNode('.'),
  )
  panel.push(linksParagraph)
  return createAccordion({
    className: 'verify-section',
    title: 'Verify it yourself',
    summary: 'The page checked the signature for you. You can check it too.',
    panel,
  })
}

const createSelfHostSection = (): HTMLElement =>
  createAccordion({
    className: 'self-host-section',
    title: 'Run your own backend',
    summary: 'Use a bridge indexer and quote service that you run yourself.',
    panel: [
      createTextElement(
        'p',
        'The interface can also talk to a bridge indexer and quote service you run yourself, instead of the ones Gibs Finance operates.',
      ),
      createLink(SELF_HOST_DOCS_URL, 'Self-host deploy docs', 'self-host-link'),
    ],
  })

/** The size of each snapshot file in pixels. Set on the image so the page does not shift as it loads. */
const SNAPSHOT_WIDTH = 660
const SNAPSHOT_HEIGHT = 399

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'

/** A small download-arrow picture for a download link. Decorative, so hidden from screen readers. */
const createDownloadIcon = (): SVGSVGElement => {
  const icon = document.createElementNS(SVG_NAMESPACE, 'svg')
  icon.setAttribute('viewBox', '0 0 16 16')
  icon.setAttribute('width', '16')
  icon.setAttribute('height', '16')
  icon.setAttribute('aria-hidden', 'true')
  icon.setAttribute('class', 'download-icon')
  const path = document.createElementNS(SVG_NAMESPACE, 'path')
  path.setAttribute('d', 'M8 2v8m0 0l-3-3m3 3l3-3M3 13h10')
  path.setAttribute('fill', 'none')
  path.setAttribute('stroke', 'currentColor')
  path.setAttribute('stroke-width', '1.5')
  path.setAttribute('stroke-linecap', 'round')
  path.setAttribute('stroke-linejoin', 'round')
  icon.append(path)
  return icon
}

/**
 * The channel toggle: one button per channel, the selected one pressed.
 * Never a `<select>` — this repository forbids them. Draws nothing when the
 * list has a single channel, since a toggle with one option offers no choice.
 */
const createChannelToggle = (view: VerifiedView): HTMLElement | null => {
  const channels = listChannels(view.manifest)
  if (channels.length < 2) {
    return null
  }
  const group = createElement('div', 'channel-toggle')
  group.setAttribute('role', 'group')
  group.setAttribute('aria-label', 'Release channel')
  for (const channel of channels) {
    const button = createTextElement('button', channelLabel(channel), 'channel-option')
    button.type = 'button'
    button.dataset.channel = channel
    button.title = `Newest release on the ${channel} branch`
    button.setAttribute('aria-pressed', String(channel === view.selectedChannel))
    button.addEventListener('click', () => {
      if (channel === view.selectedChannel) {
        return
      }
      view.onSelectChannel(channel)
    })
    group.append(button)
  }
  return group
}

/** The quiet control that asks the page to probe for a local copy. */
const createCheckLocalCopyButton = (
  localCopy: LocalCopyDisplay,
  onCheckLocalCopy: () => void,
): HTMLButtonElement => {
  const checking = localCopy.kind === 'checking'
  const button = createTextElement(
    'button',
    checking ? 'Checking…' : 'Check for my local copy',
    'check-local-copy-button',
  )
  button.type = 'button'
  button.disabled = checking
  button.title = `Looks for the Gibs program on this computer, at ${LOCAL_COPY_ORIGIN}. Your browser may ask first.`
  button.addEventListener('click', onCheckLocalCopy)
  return button
}

/** The "Open your local copy" link, plus a note when it serves a different release. */
const createRunningLocalCopy = (servedReleaseId: string, shownRelease: ReleaseEntry): HTMLElement[] => {
  const link = createLink(LOCAL_COPY_URL, 'Open your local copy', 'local-copy-button')
  link.title = `Served from your own computer at ${LOCAL_COPY_ORIGIN}. You control it.`
  if (servedReleaseId === shownRelease.id) {
    return [link]
  }
  const note = createTextElement('p', `Your copy runs ${servedReleaseId}`, 'local-copy-note')
  note.title = `Your local copy serves a different release than ${shownRelease.id}, shown above.`
  return [link, note]
}

/**
 * The contents of the local-copy slot inside the hero. The page never
 * waits on a probe, and never shows a link to a copy that did not answer.
 */
const createLocalCopyContents = (
  localCopy: LocalCopyDisplay,
  shownRelease: ReleaseEntry,
  onCheckLocalCopy: () => void,
): HTMLElement[] => {
  if (localCopy.kind === 'running') {
    return createRunningLocalCopy(localCopy.servedReleaseId, shownRelease)
  }
  const button = createCheckLocalCopyButton(localCopy, onCheckLocalCopy)
  if (localCopy.kind !== 'not-found') {
    return [button]
  }
  const note = createTextElement('span', 'Not found', 'local-copy-not-found')
  note.title = 'Nothing answered. Start the Gibs program from "Run it yourself", then check again.'
  return [button, note]
}

/** The live handle of each "Open app" group on the page, so a settled probe can reach it. */
const openAppGroups = new WeakMap<Element, OpenAppGroup>()

/**
 * Fills the hero's local-copy slot in place, without redrawing the page —
 * so a probe that settles late never closes a panel the reader has opened.
 * A no-op when the page shows no verified view.
 */
export const renderLocalCopy = (
  root: HTMLElement,
  localCopy: LocalCopyDisplay,
  shownRelease: ReleaseEntry,
  onCheckLocalCopy: () => void,
): void => {
  const slot = root.querySelector('.local-copy-slot')
  if (slot === null) {
    return
  }
  slot.replaceChildren(...createLocalCopyContents(localCopy, shownRelease, onCheckLocalCopy))
  const group = root.querySelector('.open-app-group')
  if (group !== null) {
    openAppGroups.get(group)?.setLocalCopyRunning(localCopy.kind === 'running')
  }
}

/**
 * "Run it yourself": one download per platform build of the local server,
 * then the checksums file. Every URL comes from `config.ts`, where the
 * release workflow's asset names live.
 */
const createRunItYourselfSection = (): HTMLElement => {
  const list = createElement('ul', 'download-list')
  for (const download of LOCAL_COPY_DOWNLOADS) {
    const item = createElement('li')
    const link = createLink(buildLocalCopyDownloadUrl(download.asset), download.label)
    link.prepend(createDownloadIcon())
    link.title = download.asset
    item.append(link)
    list.append(item)
  }
  const checksumsItem = createElement('li')
  const checksumsLink = createLink(buildLocalCopyDownloadUrl(LOCAL_COPY_CHECKSUMS_ASSET), 'Checksums')
  checksumsLink.prepend(createDownloadIcon())
  checksumsLink.title = 'sha256 of every download, to check the file you got'
  checksumsItem.append(checksumsLink)
  list.append(checksumsItem)

  return createAccordion({
    className: 'run-it-yourself-section',
    title: 'Run it yourself',
    summary:
      'Download a small program that serves the checked interface from your own computer.',
    panel: [list],
  })
}

/** Links to community-run sites. Draws nothing when the list is empty. */
const createCommunitySection = (communitySites: readonly CommunitySite[]): HTMLElement | null => {
  if (communitySites.length === 0) {
    return null
  }
  const section = createElement('section', 'community-section')
  section.append(createTextElement('h2', 'Community sites'))
  const list = createElement('ul', 'gateway-list')
  for (const site of communitySites) {
    const item = createElement('li')
    item.append(createLink(site.url, site.name))
    list.append(item)
  }
  section.append(list)
  return section
}

/** One card: a bold title and a short summary. The whole card is the link. */
const createCard = (card: HomeCard): HTMLAnchorElement => {
  const link = createLink(card.href, '', 'card')
  link.append(
    createTextElement('span', card.title, 'card-title'),
    createTextElement('span', card.summary, 'card-summary'),
  )
  return link
}

/** The "I want to" section: a heading, then one joined button group of bridge, build, and learn. */
const createWantSection = (cards: readonly HTMLAnchorElement[]): HTMLElement => {
  const section = createElement('section', 'want-section')
  const heading = createTextElement('h2', 'I want to', 'want-heading')
  heading.id = 'want-heading'
  const grid = createElement('div', 'card-grid')
  grid.setAttribute('role', 'group')
  grid.setAttribute('aria-labelledby', heading.id)
  grid.append(...cards)
  section.append(heading, grid)
  return section
}

/**
 * The picture of the app on the release card. It is a link that does what
 * "Open app" does: the page points it at the same address as the button, so
 * the address is worked out in one place. Two pictures, light and dark; the
 * stylesheet shows the one that matches the page theme.
 */
/**
 * The two pictures, imported so the build gives each a content-hashed name.
 *
 * WHY HASHED. Under a fixed name, Cloudflare kept serving the old picture for
 * its four-hour cache after a new one was deployed. A new picture now gets a
 * new name, so the cache can never show a stale one.
 */
const SNAPSHOT_SOURCES: Record<'light' | 'dark', string> = { light: snapshotLight, dark: snapshotDark }

const createSnapshotLink = (): HTMLAnchorElement => {
  const link = createElement('a', 'hero-snapshot')
  for (const theme of ['light', 'dark'] as const) {
    const image = createElement('img', `snapshot-image snapshot-${theme}`)
    image.src = SNAPSHOT_SOURCES[theme]
    image.alt = 'The bridge form in the app, with a chain and token picker and an amount field.'
    image.width = SNAPSHOT_WIDTH
    image.height = SNAPSHOT_HEIGHT
    image.decoding = 'async'
    link.append(image)
  }
  return link
}

/** The app of `release` on its own IPFS content address, on a neutral public gateway. This is the default way to open the app. */
const ipfsAppUrl = (manifest: VersionManifest, release: ReleaseEntry): string =>
  `${buildNeutralReleaseUrl(manifest, release.cid)}${APP_ENTRY_ROUTE}`

/**
 * The "Open app" control. On the default channel it is the button group, and
 * the reader's method decides the address. On another channel (staging) it is
 * one plain link: staging has a single address, so no method applies.
 */
const createOpenAppControl = (
  view: VerifiedView,
  shownRelease: ReleaseEntry,
  onOpenTarget: (target: OpenTarget) => void,
): HTMLElement => {
  if (view.selectedChannel !== view.manifest.defaultChannel) {
    const href = buildOpenAppUrl(view.selectedChannel, view.manifest.defaultChannel, ipfsAppUrl(view.manifest, shownRelease))
    onOpenTarget({ href, methodTitle: channelLabel(view.selectedChannel) })
    return createLink(href, 'Open app', 'open-app-button open-app-single')
  }
  const group = createOpenAppGroup({
    context: {
      manifest: view.manifest,
      cid: shownRelease.cid,
      localCopyRunning: view.localCopy.kind === 'running',
    },
    storage: view.storage ?? browserStorage,
    onOpenTarget,
  })
  openAppGroups.set(group.element, group)
  return group.element
}

/**
 * The release section: the app snapshot first, then the label, the version,
 * the Content ID, the "Open app" button, and the local-copy control below the
 * button. It has no card around it. The snapshot and the "I want to" bridge
 * card follow the "Open app" button, so all three open the same place.
 */
const createHero = (
  view: VerifiedView,
  shownRelease: ReleaseEntry,
  openAppFollowers: readonly ((target: OpenTarget) => void)[],
  snapshot: HTMLAnchorElement,
): HTMLElement => {
  const hero = createElement('div', 'hero')
  const body = createElement('div', 'hero-body')
  body.append(createTextElement('p', 'Latest signed release', 'card-eyebrow'))
  body.append(createReleaseSummary(shownRelease, createChannelToggle(view)))
  const actions = createElement('div', 'hero-actions')
  const localCopySlot = createElement('div', 'local-copy-slot')
  localCopySlot.append(
    ...createLocalCopyContents(view.localCopy, shownRelease, view.onCheckLocalCopy),
  )
  actions.append(
    createOpenAppControl(view, shownRelease, (target) => {
      for (const follow of openAppFollowers) {
        follow(target)
      }
    }),
    localCopySlot,
  )
  body.append(actions)
  hero.append(snapshot, body)
  return hero
}

const createVerifiedView = (view: VerifiedView): HTMLElement => {
  const container = createElement('section', 'verified-view')
  const shownRelease = findChannelRelease(view.manifest, view.selectedChannel)

  if (shownRelease === undefined) {
    // versionManifestSchema's own superRefine already guarantees the
    // default channel's release exists in `releases[]` for anything
    // verifyRelease accepted, and the toggle offers only channels the list
    // defines — so this is unreachable in practice. It is here only so this
    // function has no way to silently render an empty page if that
    // invariant is ever loosened without this file being revisited.
    container.append(
      createStatusView(
        'The release list has no release to show.',
        'The signature verified, but the list names no usable release yet.',
        'empty',
      ),
    )
    return container
  }

  const cardLinks = buildHomeCards(
    buildOpenAppUrl(view.selectedChannel, view.manifest.defaultChannel, ipfsAppUrl(view.manifest, shownRelease)),
    buildNeutralReleaseUrl(view.manifest, shownRelease.cid),
  ).map(createCard)
  const snapshot = createSnapshotLink()
  const followSnapshot = (target: OpenTarget): void => {
    snapshot.href = target.href
    snapshot.setAttribute('aria-label', `Open app (${target.methodTitle})`)
  }
  // The bridge card is first in the list, and opens the app like the button.
  const followBridgeCard = (target: OpenTarget): void => {
    const [bridgeCard] = cardLinks
    if (bridgeCard !== undefined) {
      bridgeCard.href = target.href
    }
  }
  container.append(createHero(view, shownRelease, [followSnapshot, followBridgeCard], snapshot))
  container.append(createWantSection(cardLinks))

  const gatewayList = createGatewayList(view.manifest, shownRelease.cid)
  const rows = createElement('div', 'accordion-group')
  rows.append(
    createRunItYourselfSection(),
    createAccordion({
      className: 'gateways-section',
      title: 'Open a specific gateway',
      summary: 'The same release, by its Content ID, on each gateway that serves it.',
      panel: [gatewayList],
    }),
    createOlderReleasesSection(view.manifest, shownRelease),
    createVerifyYourselfSection(shownRelease.cid),
    createSelfHostSection(),
  )
  container.append(rows)

  const communitySection = createCommunitySection(view.communitySites)
  if (communitySection !== null) {
    container.append(communitySection)
  }

  return container
}

/** Redraws `root` for the given state. Called once per state transition. */
export const renderPage = (root: HTMLElement, state: PageState): void => {
  switch (state.kind) {
    case 'loading':
      root.replaceChildren(createLoadingView())
      return
    case 'unreachable':
      root.replaceChildren(createUnreachableView())
      return
    case 'verification-failed':
      root.replaceChildren(createVerificationFailedView(state.reason))
      return
    case 'verified':
      root.replaceChildren(createVerifiedView(state))
      return
    default:
      state satisfies never
  }
}

/** The manifest fetch URL, shown in the page footer so a reader can see exactly what was checked. */
export const manifestSourceUrl = MANIFEST_URL
